import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AppShell from "@/layout/AppShell";
import DashboardLayout from "@/layout/DashboardLayout";
import PublicLayout from "@/layout/PublicLayout";
import { ROUTES } from "@/constants/routes";
import AuthenticatedRoute from "@/router/AuthenticatedRoute";
import ModuleGuestRoute from "@/router/ModuleGuestRoute";
import ModuleProtectedRoute from "@/router/ModuleProtectedRoute";
import ProtectedRoute from "@/router/ProtectedRoute";
import { renderSharedPublicRoutes } from "@/router/SharedPublicRoutes";

const ForgotPasswordPage = lazy(() => import("../modules/auth/pages/ForgotPasswordPage"));
const LoginPage = lazy(() => import("../modules/auth/pages/LoginPage"));
const MagicLinkLoginPage = lazy(() => import("../modules/auth/pages/MagicLinkLoginPage"));
const RegisterPage = lazy(() => import("../modules/auth/pages/RegisterPage"));
const ResetPasswordPage = lazy(() => import("../modules/auth/pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("../modules/auth/pages/VerifyEmailPage"));
const ProfilePage = lazy(() => import("../modules/portal/user/profile/pages/ProfilePage"));
const UserTrackerPage = lazy(() => import("../modules/portal/user/tracker/pages/UserTrackerPage"));
const UserTrackerInsightsPage = lazy(
  () => import("../modules/portal/user/tracker/pages/UserTrackerInsightsPage"),
);
const AppointmentsPage = lazy(
  () => import("../modules/portal/user/providerdetail/pages/AppointmentsPage"),
);
const FamilyMembersPage = lazy(
  () => import("../modules/portal/user/family-members/pages/FamilyMembersPage"),
);
const PatientSubscriptionPlansPage = lazy(
  () => import("../modules/portal/user/subscription/pages/PatientSubscriptionPlansPage"),
);
const PatientSubscriptionSuccessPage = lazy(
  () => import("../modules/portal/user/subscription/pages/PatientSubscriptionSuccessPage"),
);
const PatientSubscriptionCancelPage = lazy(
  () => import("../modules/portal/user/subscription/pages/PatientSubscriptionCancelPage"),
);
const PatientBillingPage = lazy(
  () => import("../modules/portal/user/subscription/pages/PatientBillingPage"),
);
const BookingRequestsPage = lazy(
  () => import("../modules/portal/user/providerdetail/pages/BookingRequestsPage"),
);
const BookingRequestDetailPage = lazy(
  () => import("../modules/portal/user/providerdetail/pages/BookingRequestDetailPage"),
);
const SavedProvidersPage = lazy(
  () => import("../modules/portal/user/providerdetail/pages/SavedProvidersPage"),
);
const MessagesPage = lazy(() => import("../modules/portal/user/providerdetail/pages/MessagesPage"));
const MyRecentSearchHistoriesPage = lazy(
  () => import("../modules/portal/user/searchHistory/pages/MyRecentSearchHistoriesPage"),
);
const ProviderLoginPage = lazy(() => import("@/modules/portal/provider/pages/ProviderLoginPage"));
const AdminLoginPage = lazy(() => import("@/modules/portal/admins/pages/AdminLoginPage"));
const ProviderAppointmentsPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderAppointmentsPage"),
);
const ProviderLandingPage = lazy(() => import("@/modules/portal/provider/pages/ProviderLandingPage"));
const ProviderSearchAnalyticsPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderSearchAnalyticsPage"),
);
const ProviderProfilePage = lazy(() => import("@/modules/portal/provider/pages/ProviderProfilePage"));
const ProviderLeadScoringPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderLeadScoringPage"),
);
const ProviderBillingPage = lazy(() => import("@/modules/portal/provider/pages/ProviderBillingPage"));
const ProviderSubscriptionPlansPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderSubscriptionPlansPage"),
);
const ProviderLeadsPage = lazy(() => import("@/modules/portal/provider/pages/ProviderLeadsPage"));
const ProviderPatientDetailPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderPatientDetailPage"),
);
const ProviderLeadDetailPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderLeadDetailPage"),
);
const ProviderSchedulePage = lazy(() => import("@/modules/portal/provider/pages/ProviderSchedulePage"));
const ProviderSupportTicketsPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderSupportTicketsPage"),
);
const ProviderMessagesPage = lazy(() => import("@/modules/portal/provider/pages/ProviderMessagesPage"));
const AdminLandingPage = lazy(() => import("@/modules/portal/admins/pages/AdminLandingPage"));
const AdminSearchAnalyticsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminSearchAnalyticsPage"),
);
const AdminEmailTrackingPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminEmailTrackingPage"),
);
const AdminEmailTemplatesPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminEmailTemplatesPage"),
);
const AdminBillingReportsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminBillingReportsPage"),
);
const AdminChargeRulesPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminChargeRulesPage"),
);
const AdminSubscriptionManagementPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminSubscriptionManagementPage"),
);
const AdminLeadsReportsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminLeadsReportsPage"),
);
const AdminProviderAccessPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminProviderAccessPage"),
);
const AdminProviderOnboardingPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminProviderOnboardingPage"),
);
const AdminInsuranceVerificationPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminInsuranceVerificationPage"),
);
const AdminProviderGrowthPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminProviderGrowthPage"),
);
const AdminProviderDemoRequestsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminProviderDemoRequestsPage"),
);
const AdminProviderSignupsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminProviderSignupsPage"),
);
const AdminServiceIntegrationsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminServiceIntegrationsPage"),
);
const AdminLeadScoringPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminLeadScoringPage"),
);
const AdminLeadScoringTesterPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminLeadScoringTesterPage"),
);
const AdminCacheManagementPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminCacheManagementPage"),
);
const AdminMfaConfigurationPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminMfaConfigurationPage"),
);
const AdminSupportTicketsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminSupportTicketsPage"),
);
const AdminTimerJobsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminTimerJobsPage"),
);
const AdminTimerJobDetailPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminTimerJobDetailPage"),
);
const AdminCommunicationCheckPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminCommunicationCheckPage"),
);
const AdminProviderCommunicationsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminProviderCommunicationsPage"),
);
const PatientRouter = () => (
  <Suspense fallback={<div className="page-container page-padding">Loading...</div>}>
    <Routes>
      <Route element={<AppShell />}>
        <Route element={<PublicLayout />}>
          {renderSharedPublicRoutes()}
        </Route>

        <Route
          element={
            <AuthenticatedRoute>
              <DashboardLayout />
            </AuthenticatedRoute>
          }
        >
          <Route path={ROUTES.login} element={<LoginPage />} />
          <Route path={ROUTES.register} element={<RegisterPage />} />
          <Route path={ROUTES.forgotPassword} element={<ForgotPasswordPage />} />
          <Route path={ROUTES.resetPassword} element={<ResetPasswordPage />} />
          <Route path={ROUTES.verifyEmail} element={<VerifyEmailPage />} />
          <Route path={ROUTES.magicLink} element={<MagicLinkLoginPage />} />
        </Route>

        <Route
          element={
            <ModuleGuestRoute>
              <DashboardLayout />
            </ModuleGuestRoute>
          }
        >
          <Route path={ROUTES.providersLogin} element={<ProviderLoginPage />} />
          <Route path={ROUTES.adminsLogin} element={<AdminLoginPage />} />
        </Route>

        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path={ROUTES.profile} element={<ProfilePage />} />
          <Route path={ROUTES.userTracker} element={<UserTrackerPage />} />
          <Route path={ROUTES.userTrackerInsights} element={<UserTrackerInsightsPage />} />
          <Route path={ROUTES.savedProviders} element={<SavedProvidersPage />} />
          <Route path={ROUTES.appointments} element={<AppointmentsPage />} />
          <Route path={ROUTES.familyMembers} element={<FamilyMembersPage />} />
          <Route path={ROUTES.subscriptions} element={<PatientSubscriptionPlansPage />} />
          <Route path={ROUTES.userBilling} element={<PatientBillingPage />} />
          <Route path={ROUTES.subscriptionsSuccess} element={<PatientSubscriptionSuccessPage />} />
          <Route path={ROUTES.subscriptionsCancel} element={<PatientSubscriptionCancelPage />} />
          <Route path={ROUTES.bookingRequests} element={<BookingRequestsPage />} />
          <Route path={ROUTES.bookingRequestDetail} element={<BookingRequestDetailPage />} />
          <Route path={ROUTES.messages} element={<MessagesPage />} />
          <Route path={ROUTES.messageThreadDetail} element={<MessagesPage />} />
          <Route
            path={ROUTES.myRecentSearchHistories}
            element={<MyRecentSearchHistoriesPage />}
          />
        </Route>

        <Route
          element={
            <ModuleProtectedRoute
              allowedRoles={["provider", "staff"]}
              loginRoute={ROUTES.providersLogin}
            >
              <DashboardLayout />
            </ModuleProtectedRoute>
          }
        >
          <Route path={ROUTES.providersHome} element={<ProviderLandingPage />} />
          <Route
            path={ROUTES.providersAnalytics}
            element={<ProviderSearchAnalyticsPage />}
          />
          <Route
            path={ROUTES.providersAppointments}
            element={<ProviderAppointmentsPage />}
          />
          <Route path={ROUTES.providersLeads} element={<ProviderLeadsPage />} />
          <Route path={ROUTES.providersMessages} element={<ProviderMessagesPage />} />
          <Route path={ROUTES.providersMessageThreadDetail} element={<ProviderMessagesPage />} />
          <Route
            path={ROUTES.providerLeadDetail}
            element={<ProviderLeadDetailPage />}
          />
          <Route path={ROUTES.providersSchedule} element={<ProviderSchedulePage />} />
          <Route
            path={ROUTES.providerPatientDetail}
            element={<ProviderPatientDetailPage />}
          />
        </Route>

        <Route
          element={
            <ModuleProtectedRoute
              allowedRoles={["provider"]}
              loginRoute={ROUTES.providersLogin}
            >
              <DashboardLayout />
            </ModuleProtectedRoute>
          }
        >
          <Route path={ROUTES.providersBilling} element={<ProviderBillingPage />} />
          <Route path={ROUTES.providersSubscriptions} element={<ProviderSubscriptionPlansPage />} />
          <Route path={ROUTES.providersStaff} element={<Navigate to={ROUTES.providersProfile} replace />} />
          <Route path={ROUTES.providersProfile} element={<ProviderProfilePage />} />
          <Route path={ROUTES.providersSettings} element={<Navigate to={ROUTES.providersProfile} replace />} />
          <Route path={ROUTES.providersManagement} element={<Navigate to={ROUTES.providersProfile} replace />} />
          <Route path={ROUTES.providersManageSpeciality} element={<Navigate to={ROUTES.providersProfile} replace />} />
          <Route path={ROUTES.providersManageInsurance} element={<Navigate to={ROUTES.providersProfile} replace />} />
          <Route path={ROUTES.providersLeadScoring} element={<ProviderLeadScoringPage />} />
          <Route path={ROUTES.providersSupport} element={<ProviderSupportTicketsPage />} />
        </Route>

        <Route
          element={
            <ModuleProtectedRoute
              allowedRoles={["admin"]}
              loginRoute={ROUTES.adminsLogin}
            >
              <DashboardLayout />
            </ModuleProtectedRoute>
          }
        >
          <Route path={ROUTES.adminsHome} element={<AdminLandingPage />} />
          <Route
            path={ROUTES.adminsAnalytics}
            element={<AdminSearchAnalyticsPage />}
          />
          <Route
            path={ROUTES.adminsEmailTracking}
            element={<AdminEmailTrackingPage />}
          />
          <Route
            path={ROUTES.adminsBillingReports}
            element={<AdminBillingReportsPage />}
          />
          {/* <Route
            path={ROUTES.adminsBillingOverview}
            element={<AdminBillingReportsPage />}
          /> */}
          <Route
            path={ROUTES.adminsChargeRules}
            element={<AdminChargeRulesPage />}
          />
          <Route
            path={ROUTES.adminsSubscriptions}
            element={<AdminSubscriptionManagementPage />}
          />
          <Route
            path={ROUTES.adminsLeadsReports}
            element={<AdminLeadsReportsPage />}
          />
          <Route
            path={ROUTES.adminsProviderOnboarding}
            element={<AdminProviderOnboardingPage />}
          />
          <Route
            path={ROUTES.adminsInsuranceVerification}
            element={<AdminInsuranceVerificationPage />}
          />
          <Route
            path={ROUTES.adminsProviderGrowth}
            element={<AdminProviderGrowthPage />}
          />
          <Route
            path={ROUTES.adminsProviderAccess}
            element={<AdminProviderAccessPage />}
          />
          <Route
            path={ROUTES.adminsProviderDemoRequests}
            element={<AdminProviderDemoRequestsPage />}
          />
          <Route
            path={ROUTES.adminsProviderSignups}
            element={<AdminProviderSignupsPage />}
          />
          <Route
            path={ROUTES.adminsServiceIntegrations}
            element={<AdminServiceIntegrationsPage />}
          />
          <Route
            path={ROUTES.adminsEmailTemplates}
            element={<AdminEmailTemplatesPage />}
          />
          <Route
            path={ROUTES.adminsLeadScoring}
            element={<AdminLeadScoringPage />}
          />
          <Route
            path={ROUTES.adminsLeadScoringTester}
            element={<AdminLeadScoringTesterPage />}
          />
          <Route
            path={ROUTES.adminsCacheManagement}
            element={<AdminCacheManagementPage />}
          />
          <Route
            path={ROUTES.adminsMfaConfiguration}
            element={<AdminMfaConfigurationPage />}
          />
          <Route
            path={ROUTES.adminsCommunicationCheck}
            element={<AdminCommunicationCheckPage />}
          />
          <Route
            path={ROUTES.adminsEmailTest}
            element={<Navigate to={ROUTES.adminsCommunicationCheck} replace />}
          />
          <Route
            path={ROUTES.adminsProviderCommunications}
            element={<AdminProviderCommunicationsPage />}
          />
          <Route path={ROUTES.adminsSupport} element={<AdminSupportTicketsPage />} />
          <Route path={ROUTES.adminsTimerJobs} element={<AdminTimerJobsPage />} />
          <Route path={ROUTES.adminsTimerJobDetail} element={<AdminTimerJobDetailPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to={ROUTES.home} replace />} />
    </Routes>
  </Suspense>
);

export default PatientRouter;
